import theme from '@/shared/theme';
import { CourseType } from '../types/course';
import { CardColorsMetadata, CourseData, CourseHighlight } from '@/shared/types';

const setImportant = (string: string) => `${string} !important`;

const colorMapping: Record<CourseHighlight, Record<CourseType, CardColorsMetadata>> & {
  isCourseStarted: CardColorsMetadata;
} = {
  highlightedCourses: {
    Serie: {
      cardBg: theme.palette.primary.dark,
      courseTypeChipBg: theme.palette.primary.contrastText,
      courseTypeChipTextColor: theme.palette.primary.dark,
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.primary.contrastText,
      courseLevelChipTextColor: theme.palette.primary.main,
      genreChipColor: theme.palette.primary.contrastText,
    },
    Percorsi: {
      cardBg: theme.palette.complementary.main,
      courseTypeChipBg: theme.palette.primary.contrastText,
      courseTypeChipTextColor: '#C88D06',
      cardTextColor: '#412107',
      courseLevelChipBgColor: '#412107',
      courseLevelChipTextColor: theme.palette.complementary.main,
      genreChipColor: '#371765',
    },
    Lesson: {
      cardBg: theme.palette.secondary.main,
      courseTypeChipBg: theme.palette.secondary.main,
      courseTypeChipTextColor: theme.palette.secondary.contrastText,
      cardTextColor: theme.palette.secondary.contrastText,
      courseLevelChipBgColor: theme.palette.secondary.contrastText,
      courseLevelChipTextColor: theme.palette.secondary.main,
      genreChipColor: theme.palette.secondary.contrastText,
    },
    Masterclass: {
      cardBg: theme.palette.secondary.main,
      courseTypeChipBg: theme.palette.primary.contrastText,
      courseTypeChipTextColor: theme.palette.secondary.dark,
      cardTextColor: theme.palette.secondary.contrastText,
      courseLevelChipBgColor: theme.palette.secondary.contrastText,
      courseLevelChipTextColor: theme.palette.secondary.main,
      genreChipColor: theme.palette.secondary.contrastText,
    },
  },
  regularCourses: {
    Masterclass: {
      cardBg: theme.palette.paper.main,
      courseTypeChipBg: theme.palette.secondary.main,
      courseTypeChipTextColor: theme.palette.secondary.contrastText,
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.neutral.main,
      courseLevelChipTextColor: theme.palette.primary.contrastText,
      genreChipColor: theme.palette.primary.contrastText,
    },
    Percorsi: {
      cardBg: theme.palette.paper.main,
      courseTypeChipBg: theme.palette.complementary.main,
      courseTypeChipTextColor: '#412107',
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.neutral.main,
      courseLevelChipTextColor: theme.palette.primary.contrastText,
      genreChipColor: theme.palette.primary.contrastText,
    },
    Lesson: {
      cardBg: theme.palette.paper.main,
      courseTypeChipBg: theme.palette.secondary.main,
      courseTypeChipTextColor: theme.palette.secondary.contrastText,
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.neutral.main,
      courseLevelChipTextColor: theme.palette.primary.contrastText,
      genreChipColor: theme.palette.primary.contrastText,
    },
    Serie: {
      cardBg: theme.palette.paper.main,
      courseTypeChipBg: theme.palette.primary.main,
      courseTypeChipTextColor: theme.palette.primary.contrastText,
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.neutral.main,
      courseLevelChipTextColor: theme.palette.primary.contrastText,
      genreChipColor: theme.palette.primary.contrastText,
    },
  },
  isCourseStarted: {
    cardBg: theme.palette.paper.main,
    courseTypeChipBg: theme.palette.primary.main,
    courseTypeChipTextColor: theme.palette.primary.contrastText,
    cardTextColor: theme.palette.primary.contrastText,
    courseLevelChipBgColor: theme.palette.primary.contrastText,
    courseLevelChipTextColor: theme.palette.primary.contrastText,
    genreChipColor: '',
  },
};

const applyImportantToMetadata = (metadata: CardColorsMetadata): CardColorsMetadata => ({
  cardBg: setImportant(metadata.cardBg),
  courseTypeChipBg: setImportant(metadata.courseTypeChipBg),
  courseTypeChipTextColor: setImportant(metadata.courseTypeChipTextColor),
  cardTextColor: setImportant(metadata.cardTextColor),
  courseLevelChipBgColor: setImportant(metadata.courseLevelChipBgColor),
  courseLevelChipTextColor: setImportant(metadata.courseLevelChipTextColor),
  genreChipColor: setImportant(metadata.genreChipColor),
});

export const getCardColors = (
  courseType?: CourseType,
  courseHighlight?: boolean,
  isCoureStarted?: boolean,
): CardColorsMetadata | undefined => {
  if (isCoureStarted) {
    return applyImportantToMetadata(colorMapping.isCourseStarted);
  }
  const highlightType: CourseHighlight = courseHighlight ? 'highlightedCourses' : 'regularCourses';
  if (!courseType) return undefined;
  const metadata = colorMapping[highlightType]?.[courseType];
  return metadata ? applyImportantToMetadata(metadata) : undefined;
};

export const getInstructorName = (instructors: CourseData['instructors']) => {
  if (!instructors) return '';
  if (instructors.length === 1) {
    return instructors[0].name;
  } else if (instructors.length > 1) {
    return `${instructors.length} Insegnanti`;
  } else {
    return '';
  }
};

export function toggleFilterValue(
  currentValues: string[],
  value: string,
  checked: boolean,
): string[] {
  return checked
    ? Array.from(new Set([...currentValues, value]))
    : currentValues.filter((v) => v !== value);
}
